import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plane, Luggage, Globe2, Lightbulb, Telescope, Newspaper, Mail,
  ClipboardList, Compass, ShieldCheck, Camera, MapPin, Quote,
  Twitter, Facebook, Linkedin, MessageCircle, Link2, Check,
} from "lucide-react";
import { PageHeader, vibrantTheme } from "../components/common";
import { SITE } from "../lib/site";

/* Press Release page (/press) — official announcement, linked from the footer of all pages. */

const PRESS_TITLE = "Traveler Pulse™ Brings the Vast World of Travel Information, Tools and Personal Trip Planning Into One Travel Hub";
const PRESS_DESC = "A new traveler-focused platform aims to simplify everything from trip planning and packing to airline, airport, safety, travel requirements and personal travel records. Portland, Oregon — August 15, 2026.";

const ShareBar = ({ testid = "press-share" }) => {
  const [copied, setCopied] = useState(false);
  const url = `${SITE.url}/press`;
  const enc = encodeURIComponent;
  const links = [
    ["X (Twitter)", Twitter, `https://twitter.com/intent/tweet?text=${enc(PRESS_TITLE)}&url=${enc(url)}`],
    ["Facebook", Facebook, `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`],
    ["LinkedIn", Linkedin, `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`],
    ["WhatsApp", MessageCircle, `https://wa.me/?text=${enc(`${PRESS_TITLE} — ${url}`)}`],
    ["Email", Mail, `mailto:?subject=${enc(`Press Release: ${SITE.name}`)}&body=${enc(`${PRESS_TITLE}\n\n${PRESS_DESC}\n\nRead the full release: ${url}`)}`],
  ];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { console.warn("Clipboard copy failed:", err); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground mr-1">Share</span>
      {links.map(([label, Icon, href]) => (
        <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={`Share on ${label}`} title={`Share on ${label}`}
          data-testid={`${testid}-${label.split(" ")[0].toLowerCase().replace("(", "")}`}
          className="w-9 h-9 flex items-center justify-center border border-zinc-300 text-zinc-600 hover:text-white hover:bg-[#1D4ED8] hover:border-[#1D4ED8] transition-colors">
          <Icon className="w-4 h-4" />
        </a>
      ))}
      <button onClick={copy} data-testid={`${testid}-copy`} title="Copy link"
        className={`h-9 inline-flex items-center gap-1.5 px-3 border text-xs font-700 transition-colors ${copied ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-zinc-300 text-zinc-600 hover:border-[#1D4ED8] hover:text-[#1D4ED8]"}`}>
        {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />} {copied ? "Copied!" : "Copy link"}
      </button>
    </div>
  );
};

const COVERAGE = [
  ["Airlines", "histories, fleets, routes, hubs, lounges, loyalty programs, partners, safety information and more"],
  ["Airports", "terminals, airlines, destinations, transportation, maps, parking, lounges, services and traveler information"],
  ["Flights & Trains", "trip details, schedules, flight status and personal itinerary information"],
  ["Destinations", "cities, countries, attractions, transportation, weather and travel information"],
  ["Passport & Visa Information", "helping travelers understand documents and entry requirements"],
  ["Baggage Information", "carry-on dimensions, weight limits, checked baggage, fees and restricted items"],
  ["Travel Safety", "practical guidance for emergencies, evacuation, lost baggage and other unexpected situations"],
  ["Travel Health", "information about jet lag, long flights, hydration, DVT awareness, medications and travel medical insurance"],
  ["Family Travel", "resources for traveling with children and infants"],
  ["Pet Travel", "information for travelers taking pets on their journeys"],
  ["Technology & Connectivity", "phones, Wi-Fi, chargers, international adapters and battery safety"],
  ["Loyalty & Mileage Programs", "helping travelers discover and organize frequent-flyer programs"],
  ["Travel Credit Cards & Benefits", "information about travel-related rewards and benefits"],
  ["Weather & Conditions", "helping travelers prepare for conditions at their destinations"],
  ["Travel Tips & Guides", "practical information for before, during and after travel"],
];

const PHASES = [
  ["PLAN", "Research destinations, airlines, airports, routes and travel requirements."],
  ["PREPARE", "Organize documents, insurance, medications, packing and transportation."],
  ["TRAVEL", "Keep important flight, train, airport, contact and trip information accessible."],
  ["RESPOND", "Find practical information when facing delays, lost baggage, emergencies or unexpected situations."],
  ["REMEMBER", "Preserve travel notes, experiences, photographs and trip history for future reference."],
];

const QUESTIONS = [
  ['"What do I need to know before I go?"', "should be able to find the answer."],
  ['"What should I pack?"', "should be able to build a checklist."],
  ['"What happens if my luggage is lost?"', "should be able to find practical guidance."],
  ['"What did I do on my last trip?"', "should eventually be able to look back at their own personal travel record."],
];

const Section = ({ icon: Icon, title, children, testid }) => (
  <section data-testid={testid}>
    <div className="flex items-center gap-3 mb-4">
      <span className="p-2 text-white" style={{ backgroundColor: vibrantTheme(title).bg }}><Icon className="w-5 h-5" /></span>
      <h2 className="font-heading font-900 text-xl sm:text-2xl tracking-tight">{title}</h2>
    </div>
    <div className="space-y-3 text-zinc-700 leading-relaxed text-base">{children}</div>
  </section>
);

export default function Press() {
  // SEO: page title, meta description, Open Graph/Twitter tags + NewsArticle JSON-LD
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `Press Release — ${PRESS_TITLE} | ${SITE.brandDomain}`;

    let desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    if (!desc) { desc = document.createElement("meta"); desc.setAttribute("name", "description"); document.head.appendChild(desc); }
    desc.setAttribute("content", PRESS_DESC);

    const og = [
      ["og:title", PRESS_TITLE], ["og:description", PRESS_DESC], ["og:type", "article"],
      ["og:url", `${SITE.url}/press`], ["og:site_name", SITE.name],
      ["twitter:card", "summary"], ["twitter:title", PRESS_TITLE], ["twitter:description", PRESS_DESC],
    ];
    const created = og.map(([prop, content]) => {
      const attr = prop.startsWith("twitter:") ? "name" : "property";
      let el = document.querySelector(`meta[${attr}="${prop}"]`);
      const mine = !el;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, prop); document.head.appendChild(el); }
      el.setAttribute("content", content);
      return mine ? el : null;
    });

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "NewsArticle",
      headline: PRESS_TITLE, description: PRESS_DESC, datePublished: "2026-08-15",
      author: { "@type": "Organization", name: "Traveler Pulse" },
      publisher: { "@type": "Organization", name: "Traveler Pulse", url: SITE.url },
      mainEntityOfPage: `${SITE.url}/press`,
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (prevDesc) desc.setAttribute("content", prevDesc);
      created.forEach((el) => el && el.remove());
      ld.remove();
    };
  }, []);

  return (
    <div data-testid="press-page">
      <PageHeader eyebrow="Press Release — For Immediate Release" title="One Travel Hub for the Vast World of Travel" entityCode="PRESS"
        unsplashQuery="airplane wing sunset travel"
        subtitle="Traveler Pulse™ brings the vast world of travel information, tools and personal trip planning into one Travel Hub — simplifying everything from trip planning and packing to airline, airport, safety, travel requirements and personal travel records.">
        <div className="mt-5 font-mono text-sm tracking-[0.18em] text-[#F4C77B]" data-testid="press-motto">
          Plan Better • Pack Smarter • Travel Safer • Remember More
        </div>
      </PageHeader>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12 space-y-14">
        {/* DATELINE + LEDE */}
        <section className="space-y-4 text-zinc-700 leading-relaxed" data-testid="press-lede">
          <div className="text-xs font-mono uppercase tracking-widest text-primary flex items-center gap-2">
            <Newspaper className="w-4 h-4" /> For Immediate Release
          </div>
          <p>
            <span className="font-700 text-zinc-900">Portland, Oregon, USA — August 15, 2026</span> — Traveler Pulse™ is
            being developed as a comprehensive digital travel platform designed around a simple idea: travelers
            shouldn't have to search dozens of websites, apps and documents to find the information they need for one
            journey.
          </p>
          <p>
            From choosing an airline and understanding baggage rules to checking passport requirements, preparing a
            packing list, tracking a flight, organizing travel documents and preserving memories after returning home,
            Traveler Pulse™ brings an extensive range of travel information and practical tools together in one place.
          </p>
          <div className="pt-2 border-t border-zinc-200">
            <ShareBar testid="press-share-top" />
          </div>
        </section>

        <Section icon={Globe2} title="The Vastness of Travel Information — Simplified" testid="press-vastness">
          <p>
            Travel generates an enormous amount of information. Travelers may need to research airlines, airports,
            destinations, flights, trains, baggage allowances, visa requirements, passports, travel insurance, health
            and safety, hotels, transportation, loyalty programs, weather, emergency procedures and countless other
            details.
          </p>
          <p>
            Traveler Pulse™ is designed to organize this vast information around the traveler and the journey—not
            around disconnected websites and databases.
          </p>
          <blockquote className="border-l-4 border-[#F4C77B] bg-zinc-50 px-5 py-4">
            <Quote className="w-4 h-4 text-[#c99a3f] mb-2" />
            <p className="font-heading font-700 text-lg text-zinc-800 leading-snug">
              "Our goal is to make travel information easier to discover, understand and use. A traveler should be
              able to start with an idea for a trip and find the information, tools and personal organization they
              need all the way through the journey and back home."
            </p>
            <footer className="mt-2 text-sm text-muted-foreground">
              — <Link to="/about/founder" className="text-primary font-700 hover:underline">Tom (Thomas) Vellaringattu</Link>,
              founder and creator of the Traveler Pulse™ concept
            </footer>
          </blockquote>
        </Section>

        <Section icon={Plane} title="One Place for the Entire Journey" testid="press-coverage">
          <p>Traveler Pulse™ brings together resources and features covering:</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {COVERAGE.map(([title, desc]) => (
              <div key={title} className="border border-zinc-300 bg-white p-3">
                <div className="text-sm font-700 text-zinc-900">{title}</div>
                <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Luggage} title="Introducing My Travel Hub" testid="press-travel-hub">
          <p>At the heart of Traveler Pulse™ is the concept of a personal <Link to="/travel-hub" className="text-primary font-700 hover:underline">Travel Hub</Link>.</p>
          <p>Travelers can create a trip and organize important information in one place, including:</p>
          <p className="font-mono text-sm font-700 text-zinc-900 leading-relaxed">
            Trip name • dates • flights • trains • co-travelers • contacts • notes • photos • documents •
            packing lists • reservations • loyalty/mileage programs • travel history
          </p>
          <div className="border border-zinc-300 bg-white p-6 space-y-3">
            <div className="flex items-center gap-2 font-heading font-700 text-lg text-zinc-900">
              <ClipboardList className="w-5 h-5 text-primary" /> Smart Packing Lists
            </div>
            <p className="text-sm">
              Travelers can create a simple, personalized packing list based on their journey and then track each
              item: <span className="font-700">Item • Packed / Not Packed • Location • Date Packed • Notes</span>
            </p>
            <p className="text-sm">Items can be assigned to:</p>
            <div className="flex flex-wrap gap-1.5">
              {["On Person", "Personal Item", "Carry-on", "Checked Bag 1", "Checked Bag 2", "Other"].map((l) => (
                <span key={l} className="text-xs border border-zinc-200 bg-zinc-50 px-2 py-1 text-zinc-700">{l}</span>
              ))}
            </div>
            <p className="text-sm">A packing progress indicator can help travelers see exactly what remains to be packed.</p>
            <p className="text-sm border-l-2 border-rose-300 bg-rose-50/60 px-3 py-2">
              Traveler Pulse™ can also highlight important safety considerations—for example, reminding travelers
              that passports and critical documents should remain readily accessible and that power banks and spare
              lithium batteries should not be placed in checked baggage.
            </p>
          </div>
        </Section>

        <Section icon={Compass} title="From Planning to Coming Home" testid="press-phases">
          <p>Traveler Pulse™ is designed to follow the traveler through the entire journey:</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {PHASES.map(([phase, desc], i) => (
              <div key={phase} className="border border-zinc-300 bg-white p-4">
                <div className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-heading font-900 text-base tracking-wide text-primary mt-1">{phase}</div>
                <p className="text-xs text-zinc-700 mt-1.5">{desc}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Lightbulb} title="More Than a Travel Website" testid="press-more-than">
          <p>Traveler Pulse™ is envisioned as a travel information ecosystem and personal travel companion.</p>
          <p>
            Its purpose is not simply to provide more information. It is to help travelers find the right information
            at the right time and turn it into useful action.
          </p>
          <div className="space-y-2">
            {QUESTIONS.map(([q, a]) => (
              <div key={q} className="border border-zinc-300 bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                <span className="font-heading font-700 text-zinc-900 sm:w-1/2">A traveler asking {q}</span>
                <span className="text-sm text-zinc-700">{a}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* VISION + CONTACT */}
        <section className="text-white p-8 sm:p-12" style={{ backgroundColor: vibrantTheme("press-vision").bg }} data-testid="press-vision">
          <div className="flex items-center gap-3 mb-4">
            <Telescope className="w-6 h-6 text-[#F4C77B]" />
            <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight">A Vision for the Future of Travel</h2>
          </div>
          <p className="text-white/90 leading-relaxed mb-4">
            Traveler Pulse™ envisions a world where the overwhelming amount of travel information available across
            the internet becomes easier for ordinary travelers to navigate, organize and use.
          </p>
          <p className="font-heading font-700 text-xl text-[#F4C77B]">One traveler. One journey. One Travel Hub.</p>
          <div className="font-mono text-sm tracking-[0.18em] text-white/90 mt-3">
            Plan Better. Pack Smarter. Travel Safer. Remember More.
          </div>

          <div className="border-t border-white/20 pt-6 mt-8">
            <div className="text-xs font-mono uppercase tracking-widest text-[#F4C77B] mb-2">For media, partnerships, advertising and other inquiries</div>
            <div className="font-heading font-900 text-xl">Traveler Pulse™</div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-2 text-sm text-white/95">
              <a href={SITE.url} className="inline-flex items-center gap-1.5 hover:text-[#F4C77B] transition-colors" data-testid="press-site-link">
                <MapPin className="w-4 h-4" /> {SITE.brandDomain}
              </a>
              <a href={`mailto:${SITE.supportEmail}`} className="inline-flex items-center gap-1.5 hover:text-[#F4C77B] transition-colors" data-testid="press-email-link">
                <Mail className="w-4 h-4" /> {SITE.supportEmail}
              </a>
              <Link to="/advertise" className="inline-flex items-center gap-1.5 hover:text-[#F4C77B] transition-colors" data-testid="press-advertise-link">
                <Camera className="w-4 h-4" /> Advertise With Us
              </Link>
            </div>
            <div className="mt-5 bg-white/95 inline-block px-4 py-3">
              <ShareBar testid="press-share-bottom" />
            </div>
          </div>

          <p className="text-white/70 leading-relaxed mt-8 text-xs flex gap-2">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Traveler Pulse™ is a developing travel information and technology platform. Specific features,
              information sources and services may evolve as the platform develops. Travel requirements, airline
              policies, fees and other information can change; travelers should verify critical requirements with the
              appropriate airline, airport, government authority or service provider before traveling.
            </span>
          </p>
        </section>
      </div>
    </div>
  );
}
